import { 
  CollectionReference, 
  DocumentSnapshot, 
  Query, 
  QuerySnapshot,
  query, 
  limit, 
  startAfter, 
  startAt,
  orderBy,
  onSnapshot,
  getDocs,
  Unsubscribe
} from 'firebase/firestore';
import { 
  PaginationOptions, 
  PaginatedResult, 
  PaginationState,
  DEFAULT_PAGE_SIZE,
  MAX_PAGE_SIZE 
} from '@/types/pagination';

/**
 * Creates a paginated query from a collection reference or existing query
 */
export function createPaginatedQuery(
  baseQuery: CollectionReference | Query,
  options: PaginationOptions = {},
  orderByField: string = '__name__' // Default to document ID ordering
): Query {
  const pageSize = Math.min(options.limit || DEFAULT_PAGE_SIZE, MAX_PAGE_SIZE);
  
  let paginatedQuery = query(baseQuery, orderBy(orderByField), limit(pageSize));
  
  if (options.startAfter) {
    paginatedQuery = query(baseQuery, orderBy(orderByField), startAfter(options.startAfter), limit(pageSize));
  } else if (options.startBefore) {
    paginatedQuery = query(baseQuery, orderBy(orderByField), startAt(options.startBefore), limit(pageSize));
  }
  
  return paginatedQuery;
}

/**
 * Executes a paginated query and returns formatted results
 */
export async function executePaginatedQuery<T>(
  paginatedQuery: Query,
  mapFn: (doc: DocumentSnapshot) => T
): Promise<PaginatedResult<T>> {
  const snapshot = await getDocs(paginatedQuery);
  return processPaginatedSnapshot(snapshot, mapFn);
}

/**
 * Sets up a real-time listener for paginated data
 */
export function listenToPaginatedQuery<T>(
  paginatedQuery: Query,
  callback: (result: PaginatedResult<T>) => void,
  mapFn: (doc: DocumentSnapshot) => T
): Unsubscribe {
  return onSnapshot(paginatedQuery, (snapshot) => {
    const result = processPaginatedSnapshot(snapshot, mapFn);
    callback(result);
  });
}

/**
 * Processes a query snapshot and formats it as a paginated result
 */
function processPaginatedSnapshot<T>(
  snapshot: QuerySnapshot,
  mapFn: (doc: DocumentSnapshot) => T
): PaginatedResult<T> {
  const docs = snapshot.docs;
  const data = docs.map(mapFn);
  
  // Determine if there are more pages
  // Note: This is a simplified approach. In a production app, you might want
  // to query for limit + 1 items to accurately determine hasNextPage
  const hasNextPage = docs.length > 0;
  const hasPreviousPage = false; // Would need additional logic for backward pagination
  
  return {
    data,
    hasNextPage,
    hasPreviousPage,
    nextPageCursor: docs.length > 0 ? docs[docs.length - 1] : undefined,
    previousPageCursor: docs.length > 0 ? docs[0] : undefined
  };
}

/**
 * Helper class to manage pagination state
 */
export class PaginationManager {
  private state: PaginationState;
  
  constructor(pageSize: number = DEFAULT_PAGE_SIZE) {
    this.state = {
      currentPage: 0,
      pageSize: Math.min(pageSize, MAX_PAGE_SIZE),
      cursors: [null], // First page has null cursor
      hasNextPage: false,
      hasPreviousPage: false
    };
  }
  
  getCurrentState(): PaginationState {
    return { ...this.state };
  }
  
  updateState(result: PaginatedResult<unknown>): void {
    this.state.hasNextPage = result.hasNextPage;
    this.state.hasPreviousPage = result.hasPreviousPage;
    
    // Store cursor for next page
    if (result.nextPageCursor && this.state.hasNextPage) {
      const nextPageIndex = this.state.currentPage + 1;
      this.state.cursors[nextPageIndex + 1] = result.nextPageCursor;
    }
  }
  
  getNextPageOptions(): PaginationOptions {
    if (!this.state.hasNextPage) return {};
    
    const nextPageIndex = this.state.currentPage + 1;
    return {
      limit: this.state.pageSize,
      startAfter: this.state.cursors[nextPageIndex] || undefined
    };
  }
  
  getPreviousPageOptions(): PaginationOptions {
    if (!this.state.hasPreviousPage || this.state.currentPage === 0) return {};
    
    const prevPageIndex = this.state.currentPage - 1;
    return {
      limit: this.state.pageSize,
      startAfter: this.state.cursors[prevPageIndex] || undefined
    };
  }
  
  goToNextPage(): void {
    if (this.state.hasNextPage) {
      this.state.currentPage++;
      this.state.hasPreviousPage = this.state.currentPage > 0;
    }
  }
  
  goToPreviousPage(): void {
    if (this.state.hasPreviousPage && this.state.currentPage > 0) {
      this.state.currentPage--;
      this.state.hasPreviousPage = this.state.currentPage > 0;
    }
  }
  
  reset(): void {
    this.state.currentPage = 0;
    this.state.cursors = [null];
    this.state.hasNextPage = false;
    this.state.hasPreviousPage = false;
  }
}